/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable react-hooks/rules-of-hooks */
import React from "react";

import { isElectron } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";

import { SupportedTheme } from "@vienna/common";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Plate } from "src/classes/plate/Plate";
import { useHotkey } from "src/lib/hooks/useHotkey";
import { safeSegmentTrack } from "src/lib/logging/segment";
import { AppState } from "src/store";
import { setTheme } from "src/store/modules/globalUI";
import { searchActions } from "src/store/modules/search";

import { HOTKEYS } from "./hotkeys";
interface Props {
  children: React.ReactNode | React.ReactNode[];
}

export function AppHotkeys({ children }: Props) {
  // STATE
  const dispatch = useDispatch();
  const searchModalOpen = useSelector((state: AppState) => state.search.modalOpen);
  const currentUser = useCurrentUser();
  const theme = useSelector((state: AppState) => state.globalUI.uiSettings.theme);

  const createNewPage = () => {
    Plate.create().then((plate) => plate?.open());
  };

  // different ways to open the search bar
  useHotkey(HOTKEYS.SEARCH, () => toggleSearch("search"));
  useHotkey(HOTKEYS.COMMAND_PALETTE, () => toggleSearch("command"));

  if (currentUser) {
    useHotkey(HOTKEYS.CHANGE_THEME, () => {
      dispatch(setTheme(theme === "dark" ? SupportedTheme.light : SupportedTheme.dark));
    });
  }

  if (isElectron && currentUser) {
    useHotkey(HOTKEYS.NEW_PLATE, () => createNewPage(), [currentUser.snapshot.username]);
  }

  // HANDLERS
  const toggleSearch = (modalType: "search" | "command") => {
    if (searchModalOpen) {
      dispatch(searchActions.searchModalClosed({ usedHotkey: true }));
    } else {
      dispatch(searchActions.searchModalOpened({ usedHotkey: true, modalType }));
    }
    safeSegmentTrack({
      event: "Hotkey Used",
      properties: {
        action: "Search",
      },
    });
  };

  // COMMANDS
  return <>{children}</>;
}
